// src/context/AuthContext.js
//
// Provides role/permission state to the entire component tree.

// Roles are sourced from the PingOne ID token — confirmed via a live PPE
// login (2026-09-04) that memberOf is on the ID token, NOT the access
// token. react-oidc-context's user.profile IS the decoded ID token, so no
// manual jwtDecode is needed here.

import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth as useOidcAuth } from "react-oidc-context";

const ALL_PERMISSIONS = [
  "General_Limited_Group",
  "PII_Group",
  "PII_Group_Low",
  "PII_Group_Med",
  "PII_Group_FULL",
  "Ops_Group",
  "OG_Group",
  "LD_Group",
  "LD_Group_Admin",
  "TA_Group",
  "WFM_Group",
  "CP_Group",
  "ISU_PRV_Access",
  "ASD_Group",
  "WFM_IDM_Group_Admin",
  "FACBRZ_Group",
  "CoreRatio_Group",
  "LD_Leader",
  "LD_Trainer",
];

const AuthContext = createContext({
  roles: [],
  hasPermission: () => false,
  hasAnyPermission: () => false,
  isLoaded: false,
});

export const AuthProvider = ({ children }) => {
  const oidcAuth = useOidcAuth();
  const [roles, setRoles] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const loadRoles = async () => {
      if (!oidcAuth.isAuthenticated || !oidcAuth.user) {
        setIsLoaded(true);
        return;
      }

      try {
        const tokenRoles =
          oidcAuth.user.profile?.memberOf || oidcAuth.user.profile?.roles || [];
        const matched = ALL_PERMISSIONS.filter((p) => tokenRoles.includes(p));

        setRoles(matched);

        // Remove once all 25 components are migrated to useAuth().
        localStorage.setItem("Access", tokenRoles.join(","));
      } catch (err) {
        console.warn("Failed to read PingOne ID token claims:", err?.message);
      } finally {
        setIsLoaded(true);
      }
    };

    loadRoles();
  }, [oidcAuth.isAuthenticated, oidcAuth.user]);

  const hasPermission = (permission) => roles.includes(permission);

  // Useful for OR checks: hasAnyPermission(["PII_Group", "PII_Group_Low"])
  const hasAnyPermission = (permissions) =>
    permissions.some((p) => roles.includes(p));

  return (
    <AuthContext.Provider
      value={{ roles, hasPermission, hasAnyPermission, isLoaded }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
